import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import CustomerLayout from '../components/CustomerLayout';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { 
  ArrowRight, ChevronLeft, ChevronRight, ShoppingCart, Star,
  Cookie, Croissant, Flame, Shirt, Palette, Gem, Lamp, Sparkles, Leaf, 
  Image as ImageIcon, Coffee, Package, Gift, Heart, ShoppingBag, Home as HomeIcon,
  Music, BookOpen, Store, Plane, Globe, TrendingUp, Award
} from 'lucide-react';

// Icon mapping for categories
const ICON_MAP = {
  'cookie': Cookie, 'croissant': Croissant, 'flame': Flame, 'shirt': Shirt,
  'palette': Palette, 'gem': Gem, 'lamp': Lamp, 'sparkles': Sparkles,
  'leaf': Leaf, 'image': ImageIcon, 'coffee': Coffee, 'nut': Package,
  'gift': Gift, 'package': Package, 'heart': Heart, 'star': Store,
  'shopping-bag': ShoppingBag, 'home': HomeIcon, 'music': Music, 'book-open': BookOpen,
};

// Decorative SVG Components
const RangoliPattern = () => (
  <svg viewBox="0 0 100 100" className="w-full h-full" fill="currentColor">
    <circle cx="50" cy="50" r="3" />
    <circle cx="50" cy="50" r="15" fill="none" stroke="currentColor" strokeWidth="1" />
    <circle cx="50" cy="50" r="25" fill="none" stroke="currentColor" strokeWidth="0.5" />
    <circle cx="50" cy="35" r="2" />
    <circle cx="50" cy="65" r="2" />
    <circle cx="35" cy="50" r="2" />
    <circle cx="65" cy="50" r="2" />
    <circle cx="40" cy="40" r="1.5" />
    <circle cx="60" cy="40" r="1.5" />
    <circle cx="40" cy="60" r="1.5" />
    <circle cx="60" cy="60" r="1.5" />
  </svg>
);

const TempleSpire = () => (
  <svg viewBox="0 0 40 60" className="w-full h-full" fill="currentColor">
    <path d="M20 0 L25 15 L22 15 L22 20 L25 20 L25 25 L22 25 L22 30 L28 30 L28 55 L30 55 L30 60 L10 60 L10 55 L12 55 L12 30 L18 30 L18 25 L15 25 L15 20 L18 20 L18 15 L15 15 Z" />
  </svg>
);

const DivaLamp = () => (
  <svg viewBox="0 0 30 40" className="w-full h-full" fill="currentColor">
    <ellipse cx="15" cy="35" rx="12" ry="4" />
    <path d="M8 35 Q8 25 15 20 Q22 25 22 35" />
    <ellipse cx="15" cy="15" rx="3" ry="5" fillOpacity="0.5" />
    <path d="M15 10 Q13 5 15 2 Q17 5 15 10" fillOpacity="0.7" />
  </svg>
);

const PaisleySmall = () => (
  <svg viewBox="0 0 20 30" className="w-full h-full" fill="currentColor">
    <path d="M10 2 Q18 8 18 18 Q18 28 10 28 Q5 28 5 20 Q5 12 10 8 Q12 10 12 15 Q12 22 8 22 Q6 22 6 18 Q6 14 10 12" fillRule="evenodd" />
  </svg>
);

const FloralCorner = () => (
  <svg viewBox="0 0 50 50" className="w-full h-full" fill="currentColor">
    <circle cx="10" cy="10" r="3" />
    <path d="M10 5 Q15 10 10 15 Q5 10 10 5" />
    <path d="M5 10 Q10 5 15 10 Q10 15 5 10" />
    <circle cx="25" cy="5" r="2" />
    <circle cx="5" cy="25" r="2" />
    <path d="M15 15 Q25 5 35 15" fill="none" stroke="currentColor" strokeWidth="1" />
    <path d="M15 15 Q5 25 15 35" fill="none" stroke="currentColor" strokeWidth="1" />
  </svg>
);

// Splash Screen with Traditional Art
function SplashScreen({ onComplete }) {
  const [phase, setPhase] = useState(0);
  
  useEffect(() => {
    const timers = [
      setTimeout(() => setPhase(1), 300),
      setTimeout(() => setPhase(2), 800),
      setTimeout(() => setPhase(3), 1300),
      setTimeout(() => onComplete(), 2000),
    ];
    return () => timers.forEach(clearTimeout);
  }, [onComplete]);

  return (
    <div className="fixed inset-0 z-[100] bg-[#0F5132] flex items-center justify-center overflow-hidden">
      {/* Decorative traditional elements */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Corner florals */}
        <div className={`absolute top-4 left-4 w-16 h-16 text-white/10 transition-all duration-1000 ${phase >= 1 ? 'opacity-100 rotate-0' : 'opacity-0 -rotate-90'}`}>
          <FloralCorner />
        </div>
        <div className={`absolute top-4 right-4 w-16 h-16 text-white/10 transition-all duration-1000 rotate-90 ${phase >= 1 ? 'opacity-100' : 'opacity-0'}`}>
          <FloralCorner />
        </div>
        <div className={`absolute bottom-4 left-4 w-16 h-16 text-white/10 transition-all duration-1000 -rotate-90 ${phase >= 1 ? 'opacity-100' : 'opacity-0'}`}>
          <FloralCorner />
        </div>
        <div className={`absolute bottom-4 right-4 w-16 h-16 text-white/10 transition-all duration-1000 rotate-180 ${phase >= 1 ? 'opacity-100' : 'opacity-0'}`}>
          <FloralCorner />
        </div>
        
        {/* Temple spires */}
        <div className={`absolute bottom-0 left-20 w-10 h-20 text-white/5 transition-all duration-1000 delay-200 ${phase >= 2 ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
          <TempleSpire />
        </div>
        <div className={`absolute bottom-0 right-20 w-8 h-16 text-white/5 transition-all duration-1000 delay-300 ${phase >= 2 ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
          <TempleSpire />
        </div>
        
        {/* Diva lamps */}
        <div className={`absolute top-1/4 left-10 w-8 h-12 text-[#FFD700]/20 transition-all duration-700 ${phase >= 1 ? 'opacity-100' : 'opacity-0'}`}>
          <DivaLamp />
        </div>
        <div className={`absolute top-1/4 right-10 w-8 h-12 text-[#FFD700]/20 transition-all duration-700 delay-100 ${phase >= 1 ? 'opacity-100' : 'opacity-0'}`}>
          <DivaLamp />
        </div>
        
        {/* Rangoli patterns */}
        <div className={`absolute top-1/2 left-1/4 -translate-y-1/2 w-24 h-24 text-white/5 transition-all duration-1000 ${phase >= 2 ? 'scale-100 opacity-100' : 'scale-0 opacity-0'}`}>
          <RangoliPattern />
        </div>
        <div className={`absolute top-1/2 right-1/4 -translate-y-1/2 w-24 h-24 text-white/5 transition-all duration-1000 delay-200 ${phase >= 2 ? 'scale-100 opacity-100' : 'scale-0 opacity-0'}`}>
          <RangoliPattern />
        </div>
        
        {/* Paisley decorations */}
        <div className={`absolute top-20 left-1/3 w-6 h-10 text-white/10 rotate-45 transition-all duration-700 ${phase >= 1 ? 'opacity-100' : 'opacity-0'}`}>
          <PaisleySmall />
        </div>
        <div className={`absolute bottom-20 right-1/3 w-6 h-10 text-white/10 -rotate-45 transition-all duration-700 delay-100 ${phase >= 1 ? 'opacity-100' : 'opacity-0'}`}>
          <PaisleySmall />
        </div>
      </div>
      
      {/* Main content */}
      <div className="relative text-center">
        <div className={`transition-all duration-700 ${phase >= 1 ? 'opacity-100 scale-100' : 'opacity-0 scale-50'}`}>
          <div className="w-28 h-28 mx-auto mb-6 bg-white rounded-2xl shadow-2xl flex items-center justify-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_shopstack-46/artifacts/wfop58ec_image.png" 
              alt="nammahut" 
              className="w-20 h-20 object-contain"
            />
          </div>
        </div>
        
        <h1 className={`text-5xl font-bold text-white mb-2 transition-all duration-700 delay-200 ${phase >= 2 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          nammahut
        </h1>
        
        <p className={`text-lg text-[#90EE90] tracking-[0.3em] uppercase transition-all duration-700 delay-400 ${phase >= 2 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          Traditional Marketplace
        </p>
        
        {/* Loading animation with traditional dots */}
        <div className={`flex justify-center gap-3 mt-8 transition-all duration-500 delay-500 ${phase >= 3 ? 'opacity-100' : 'opacity-0'}`}>
          {[0, 1, 2, 3, 4].map((i) => (
            <div 
              key={i}
              className="w-2 h-2 bg-[#90EE90] rounded-full animate-bounce"
              style={{ animationDelay: `${i * 0.1}s` }}
            />
          ))}
        </div>
      </div>
      
      {/* Fade out */}
      <div className={`absolute inset-0 bg-[#FAFDF8] transition-opacity duration-500 ${phase >= 3 ? 'opacity-100' : 'opacity-0 pointer-events-none'}`} />
    </div>
  );
}

export default function HomePage() {
  const navigate = useNavigate();
  const [showSplash, setShowSplash] = useState(true);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [trendingProducts, setTrendingProducts] = useState([]);
  const [topSellingProducts, setTopSellingProducts] = useState([]);
  const [featuredVendors, setFeaturedVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentBanner, setCurrentBanner] = useState(0);
  const categoryScrollRef = useRef(null);
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Default banners (fallback)
  const defaultBanners = [
    {
      title: 'Handloom Heritage Sale',
      subtitle: 'Authentic sarees & textiles from master weavers',
      discount: 'FLAT 30% OFF',
      bgGradient: 'from-[#0F5132] via-[#198754] to-[#0F5132]',
      image: 'https://images.unsplash.com/photo-1610030469983-98e550d6193c?w=400&h=300&fit=crop',
      link: '/products',
      button_text: 'Shop Now'
    },
    {
      title: 'Festival Sweets Collection',
      subtitle: 'Traditional sweets made with love & pure ingredients',
      discount: 'UPTO 50% OFF',
      bgGradient: 'from-[#8B4513] via-[#D2691E] to-[#8B4513]',
      image: 'https://images.unsplash.com/photo-1589119908995-c6837fa14848?w=400&h=300&fit=crop',
      link: '/products',
      button_text: 'Shop Now'
    },
    {
      title: 'Spice Route Specials',
      subtitle: 'Authentic spices from Kerala to Kashmir',
      discount: 'FREE SHIPPING',
      bgGradient: 'from-[#0F5132] via-[#2E8B57] to-[#0F5132]',
      image: 'https://images.unsplash.com/photo-1596040033229-a9821ebd058d?w=400&h=300&fit=crop',
      link: '/products',
      button_text: 'Shop Now'
    },
  ];
  
  const [banners, setBanners] = useState(defaultBanners);

  useEffect(() => {
    const splashShown = sessionStorage.getItem('splashShown');
    if (splashShown) {
      setShowSplash(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(() => {
      setCurrentBanner(prev => (prev + 1) % banners.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [banners.length]);

  // SEO: Set homepage meta tags
  useEffect(() => {
    document.title = 'NammaHut | Traditional Indian Marketplace - Buy Authentic Sweets, Snacks & More';
    
    // Update meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.content = 'Shop authentic traditional Indian sweets, snacks, dry fruits, coffee, rice & millets from NammaHut. Buy Kovilpatti Kadalai Mittai, Tirunelveli Halwa, Thoothukudi Macaroon, Dharwad Pedha & more. Free delivery across India!';
    }
    
    // Set canonical URL
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.rel = 'canonical';
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.href = 'https://nammahut.com/';
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [categoriesRes, productsRes, vendorsRes, bannersRes] = await Promise.all([
        axios.get(`${API_URL}/api/collections`),
        axios.get(`${API_URL}/api/products?limit=50`),
        axios.get(`${API_URL}/api/vendors/featured?limit=8`),
        axios.get(`${API_URL}/api/banners`).catch(() => ({ data: [] }))
      ]);
      
      setCategories((categoriesRes.data || []).sort((a, b) => (a.display_order || 0) - (b.display_order || 0)));
      const allProducts = productsRes.data || [];
      setProducts(allProducts);
      
      // Filter trending and top-selling products
      setTrendingProducts(allProducts.filter(p => p.is_trending));
      setTopSellingProducts(allProducts.filter(p => p.is_top_selling));
      
      setFeaturedVendors(vendorsRes.data || []);
      
      // Use API banners if available, otherwise keep defaults
      if (bannersRes.data && bannersRes.data.length > 0) {
        const apiBanners = bannersRes.data.map(b => ({
          ...b,
          discount: b.title?.includes('OFF') ? b.title.split(' - ')[1] || 'SPECIAL OFFER' : b.subtitle || 'SPECIAL OFFER',
          bgGradient: 'from-[#0F5132] via-[#198754] to-[#0F5132]',
          image: b.image_url
        }));
        setBanners(apiBanners);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSplashComplete = () => {
    sessionStorage.setItem('splashShown', 'true');
    setShowSplash(false);
  };

  const scrollCategories = (direction) => {
    if (categoryScrollRef.current) {
      categoryScrollRef.current.scrollBy({
        left: direction === 'left' ? -200 : 200,
        behavior: 'smooth'
      });
    }
  };

  if (showSplash) {
    return <SplashScreen onComplete={handleSplashComplete} />;
  }

  if (loading) {
    return (
      <CustomerLayout>
        <div className="min-h-screen flex items-center justify-center bg-[#FAFDF8]">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-[#0F5132]/20 border-t-[#0F5132] rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-[#0F5132]/70">Loading...</p>
          </div>
        </div>
      </CustomerLayout>
    );
  }

  return (
    <CustomerLayout>
      <div className="bg-[#FAFDF8] min-h-screen relative">
        {/* Decorative background elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-40 right-10 w-20 h-20 text-[#0F5132]/5">
            <RangoliPattern />
          </div>
          <div className="absolute top-96 left-5 w-16 h-16 text-[#0F5132]/5">
            <RangoliPattern />
          </div>
          <div className="absolute bottom-40 right-20 w-12 h-20 text-[#0F5132]/5">
            <PaisleySmall />
          </div>
        </div>

        {/* Category Explorer Bar */}
        <div className="bg-white border-b border-[#0F5132]/10 relative">
          <div className="max-w-7xl mx-auto px-4 py-4">
            <div className="relative flex items-center">
              <button
                onClick={() => scrollCategories('left')}
                className="absolute -left-2 z-10 w-8 h-8 bg-white shadow-lg rounded-full flex items-center justify-center hover:bg-gray-50 border border-[#0F5132]/20 text-[#0F5132]"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              
              <div 
                ref={categoryScrollRef}
                className="flex gap-6 overflow-x-auto scrollbar-hide scroll-smooth mx-8 py-2"
                style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
              >
                {categories.map((category) => {
                  const IconComponent = ICON_MAP[category.icon] || Package;
                  return (
                    <Link
                      key={category.id || category._id}
                      to={`/products?category=${encodeURIComponent(category.name)}`}
                      className="flex flex-col items-center gap-2 min-w-[80px] group"
                    >
                      <div className="w-14 h-14 rounded-full bg-[#0F5132] flex items-center justify-center group-hover:bg-[#198754] group-hover:scale-110 transition-all shadow-md">
                        <IconComponent className="w-6 h-6 text-white" />
                      </div>
                      <span className="text-xs text-[#0F5132] text-center font-medium leading-tight max-w-[80px] line-clamp-2 group-hover:text-[#198754]">
                        {category.name}
                      </span>
                    </Link>
                  );
                })}
              </div>
              
              <button
                onClick={() => scrollCategories('right')}
                className="absolute -right-2 z-10 w-8 h-8 bg-white shadow-lg rounded-full flex items-center justify-center hover:bg-gray-50 border border-[#0F5132]/20 text-[#0F5132]"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 py-6 space-y-8 relative">
          {/* Hero Banner */}
          <section className="relative">
            <div className={`rounded-2xl overflow-hidden bg-gradient-to-r ${banners[currentBanner].bgGradient} transition-all duration-500 relative`}>
              {/* Decorative elements on banner */}
              <div className="absolute inset-0 overflow-hidden">
                <div className="absolute top-4 left-4 w-12 h-12 text-white/10">
                  <FloralCorner />
                </div>
                <div className="absolute top-4 right-4 w-12 h-12 text-white/10 rotate-90">
                  <FloralCorner />
                </div>
                <div className="absolute bottom-4 left-4 w-12 h-12 text-white/10 -rotate-90">
                  <FloralCorner />
                </div>
                <div className="absolute bottom-4 right-4 w-12 h-12 text-white/10 rotate-180">
                  <FloralCorner />
                </div>
                <div className="absolute top-1/2 left-8 -translate-y-1/2 w-6 h-10 text-white/10">
                  <DivaLamp />
                </div>
              </div>
              
              <div className="flex flex-col md:flex-row items-center justify-between p-8 md:p-12 gap-6 relative">
                <div className="flex-1 text-center md:text-left">
                  <Badge className="bg-[#FFD700] hover:bg-[#FFD700] text-[#0F5132] mb-4 px-4 py-1.5 text-sm font-bold rounded-full shadow-md">
                    {banners[currentBanner].discount}
                  </Badge>
                  <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-3 leading-tight">
                    {banners[currentBanner].title}
                  </h1>
                  <p className="text-base lg:text-lg text-white/80 mb-6 max-w-lg">
                    {banners[currentBanner].subtitle}
                  </p>
                  <Button 
                    onClick={() => navigate('/products')}
                    size="lg"
                    className="bg-white hover:bg-gray-100 text-[#0F5132] rounded-full px-8 py-6 text-lg shadow-lg hover:shadow-xl transition-all hover:scale-105 font-semibold"
                  >
                    Shop Now <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </div>
                <div className="hidden md:block">
                  <div className="w-72 h-56 rounded-2xl overflow-hidden shadow-2xl border-4 border-white/20 transform rotate-3 hover:rotate-0 transition-transform duration-500">
                    <img src={banners[currentBanner].image} alt="" className="w-full h-full object-cover" />
                  </div>
                </div>
              </div>
            </div>
            
            {/* Banner Dots */}
            <div className="flex justify-center gap-2 mt-4">
              {banners.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentBanner(idx)}
                  className={`h-2.5 rounded-full transition-all ${
                    idx === currentBanner ? 'w-8 bg-[#0F5132]' : 'w-2.5 bg-[#0F5132]/30 hover:bg-[#0F5132]/50'
                  }`}
                />
              ))}
            </div>
          </section>

          {/* Quick Links */}
          <section className="grid grid-cols-2 gap-4">
            <Link to="/nammayatra" className="group">
              <div className="bg-gradient-to-r from-[#D2691E] to-[#CD853F] rounded-xl p-5 text-white hover:shadow-xl transition-all hover:scale-[1.02] relative overflow-hidden">
                <div className="absolute top-2 right-2 w-8 h-8 text-white/10">
                  <TempleSpire />
                </div>
                <div className="flex items-center gap-3 relative">
                  <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
                    <Plane className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg">NammaYatra</h3>
                    <p className="text-white/80 text-sm">Traditional Travel</p>
                  </div>
                  <ArrowRight className="w-5 h-5 ml-auto group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </Link>
            <Link to="/international" className="group">
              <div className="bg-gradient-to-r from-[#1E3A5F] to-[#2C5282] rounded-xl p-5 text-white hover:shadow-xl transition-all hover:scale-[1.02]">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
                    <Globe className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg">International</h3>
                    <p className="text-white/80 text-sm">Ship Worldwide</p>
                  </div>
                  <ArrowRight className="w-5 h-5 ml-auto group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </Link>
          </section>

          {/* Shop By Vendors */}
          {featuredVendors.length > 0 && (
            <section>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 text-[#0F5132]/30">
                    <DivaLamp />
                  </div>
                  <h2 className="text-xl font-bold text-[#0F5132]">Shop By Sellers</h2>
                </div>
                <Link to="/products" className="text-[#0F5132] hover:text-[#198754] font-medium text-sm flex items-center">
                  View All <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
              <div className="grid grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-4">
                {featuredVendors.map((vendor) => (
                  <Link
                    key={vendor.id}
                    to={`/vendor/${vendor.id}/store`}
                    className="group text-center"
                  >
                    <div className="w-16 h-16 mx-auto mb-2 rounded-full bg-[#0F5132]/10 flex items-center justify-center overflow-hidden border-2 border-transparent group-hover:border-[#0F5132] group-hover:shadow-lg transition-all">
                      {vendor.logo ? (
                        <img src={vendor.logo} alt={vendor.business_name} className="w-full h-full object-cover" />
                      ) : (
                        <Store className="w-7 h-7 text-[#0F5132]" />
                      )}
                    </div>
                    <p className="text-xs font-medium text-[#0F5132]/80 line-clamp-2 group-hover:text-[#0F5132]">
                      {vendor.business_name}
                    </p>
                  </Link>
                ))}
              </div>
            </section>
          )}

          {/* Trending Products */}
          {trendingProducts.length > 0 && (
            <section>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-orange-500 rounded-lg flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-[#0F5132]">Trending Now</h2>
                    <p className="text-sm text-[#0F5132]/60">What customers are loving</p>
                  </div>
                </div>
                <Link to="/products?filter=trending" className="text-[#0F5132] hover:text-[#198754] font-medium text-sm flex items-center">
                  View All <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                {trendingProducts.slice(0, 5).map((product, idx) => (
                  <ProductCard key={product.id || product._id} product={product} index={idx} />
                ))}
              </div>
            </section>
          )}

          {/* Top Selling Products */}
          {topSellingProducts.length > 0 && (
            <section>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
                    <Award className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-[#0F5132]">Top Sellers</h2>
                    <p className="text-sm text-[#0F5132]/60">Best selling products</p>
                  </div>
                </div>
                <Link to="/products?filter=top_selling" className="text-[#0F5132] hover:text-[#198754] font-medium text-sm flex items-center">
                  View All <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                {topSellingProducts.slice(0, 5).map((product, idx) => (
                  <ProductCard key={product.id || product._id} product={product} index={idx} />
                ))}
              </div>
            </section>
          )}

          {/* Featured Products */}
          <section>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-6 h-10 text-[#0F5132]/30">
                  <PaisleySmall />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-[#0F5132]">Featured Products</h2>
                  <p className="text-sm text-[#0F5132]/60">Handpicked traditional treasures</p>
                </div>
              </div>
              <Link to="/products" className="text-[#0F5132] hover:text-[#198754] font-medium text-sm flex items-center">
                View All <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {products.slice(0, 10).map((product, idx) => (
                <ProductCard key={product.id || product._id} product={product} index={idx} />
              ))}
            </div>
          </section>

          {/* More Products */}
          {products.length > 10 && (
            <section>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 text-[#0F5132]/30">
                    <RangoliPattern />
                  </div>
                  <h2 className="text-xl font-bold text-[#0F5132]">More Products</h2>
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                {products.slice(10, 20).map((product, idx) => (
                  <ProductCard key={product.id || product._id} product={product} index={idx} />
                ))}
              </div>
            </section>
          )}
        </div>
      </div>
    </CustomerLayout>
  );
}

// Traditional Corner Decoration for Product Cards
const TraditionalCorner = ({ position }) => {
  const rotations = {
    'top-left': '',
    'top-right': 'rotate-90',
    'bottom-right': 'rotate-180',
    'bottom-left': '-rotate-90'
  };
  const positions = {
    'top-left': 'top-0 left-0',
    'top-right': 'top-0 right-0',
    'bottom-right': 'bottom-0 right-0',
    'bottom-left': 'bottom-0 left-0'
  };
  
  return (
    <div className={`absolute ${positions[position]} w-6 h-6 ${rotations[position]} pointer-events-none z-10`}>
      <svg viewBox="0 0 24 24" fill="none" className="w-full h-full">
        <path 
          d="M0 0 L8 0 Q6 1 6 3 L6 8 Q6 6 4 6 L0 6 L0 0" 
          fill="#0F5132" 
          fillOpacity="0.9"
        />
        <circle cx="3" cy="3" r="1.5" fill="#FFD700" />
        <path d="M6 1.5 Q9 3 6 6" stroke="#90EE90" strokeWidth="0.5" fill="none" />
      </svg>
    </div>
  );
};

// Traditional Border Pattern Component
const TraditionalBorder = () => (
  <div className="absolute inset-0 pointer-events-none z-10">
    {/* Top border pattern */}
    <div className="absolute top-0 left-6 right-6 h-[3px] overflow-hidden">
      <div className="flex gap-1 animate-border-flow">
        {[...Array(20)].map((_, i) => (
          <div key={i} className="flex-shrink-0 flex items-center gap-0.5">
            <div className="w-1.5 h-1.5 bg-[#0F5132] rounded-full opacity-40" />
            <div className="w-3 h-[2px] bg-gradient-to-r from-[#0F5132]/60 to-transparent" />
          </div>
        ))}
      </div>
    </div>
    {/* Bottom border pattern */}
    <div className="absolute bottom-0 left-6 right-6 h-[3px] overflow-hidden">
      <div className="flex gap-1 animate-border-flow-reverse">
        {[...Array(20)].map((_, i) => (
          <div key={i} className="flex-shrink-0 flex items-center gap-0.5">
            <div className="w-1.5 h-1.5 bg-[#0F5132] rounded-full opacity-40" />
            <div className="w-3 h-[2px] bg-gradient-to-r from-[#0F5132]/60 to-transparent" />
          </div>
        ))}
      </div>
    </div>
    {/* Left border dots */}
    <div className="absolute left-0 top-6 bottom-6 w-[3px] flex flex-col justify-evenly items-center">
      {[...Array(4)].map((_, i) => (
        <div key={i} className="w-1 h-1 bg-[#0F5132] rounded-full opacity-30" />
      ))}
    </div>
    {/* Right border dots */}
    <div className="absolute right-0 top-6 bottom-6 w-[3px] flex flex-col justify-evenly items-center">
      {[...Array(4)].map((_, i) => (
        <div key={i} className="w-1 h-1 bg-[#0F5132] rounded-full opacity-30" />
      ))}
    </div>
  </div>
);

// Product Card with traditional styling
function ProductCard({ product, index }) {
  const hasDiscount = product.sale_price && product.sale_price < product.price;
  const discountPercent = hasDiscount ? Math.round(((product.price - product.sale_price) / product.price) * 100) : 0;
  
  // Use slug for SEO-friendly URLs, fallback to id
  const productUrl = product.slug || product.id || product._id;

  return (
    <Link to={`/products/${productUrl}`} className="group">
      <Card className="overflow-visible h-full border-0 shadow-sm hover:shadow-xl transition-all bg-white group-hover:-translate-y-1 relative">
        {/* Traditional corner decorations */}
        <TraditionalCorner position="top-left" />
        <TraditionalCorner position="top-right" />
        <TraditionalCorner position="bottom-left" />
        <TraditionalCorner position="bottom-right" />
        
        {/* Traditional border pattern */}
        <TraditionalBorder />
        
        {/* Inner card with slight padding for border effect */}
        <div className="overflow-hidden rounded-lg border border-[#0F5132]/20 group-hover:border-[#0F5132]/40 transition-colors">
          <div className="relative aspect-square bg-gray-100 overflow-hidden">
            <img 
              src={product.images?.[0] || 'https://via.placeholder.com/200?text=Product'} 
              alt={product.name} 
              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" 
            />
            {hasDiscount && (
              <Badge className="absolute top-2 left-2 bg-[#0F5132] hover:bg-[#0F5132] text-white text-xs font-bold shadow-md">
                {discountPercent}% OFF
              </Badge>
            )}
            {/* Quick add overlay */}
            <div className="absolute inset-0 bg-[#0F5132]/0 group-hover:bg-[#0F5132]/10 transition-colors flex items-center justify-center">
              <Button 
                size="sm" 
                className="opacity-0 group-hover:opacity-100 transition-all transform translate-y-4 group-hover:translate-y-0 bg-white text-[#0F5132] hover:bg-gray-100 shadow-lg font-semibold"
              >
                <ShoppingCart className="w-4 h-4 mr-1" /> Add
              </Button>
            </div>
          </div>
          <CardContent className="p-3 bg-white">
            <h3 className="font-medium text-[#0F5132] text-sm line-clamp-2 mb-2 group-hover:text-[#198754] leading-tight h-10">
              {product.name}
            </h3>
            <div className="flex items-baseline gap-2">
              <span className="font-bold text-[#0F5132]">₹{(product.sale_price || product.price).toLocaleString()}</span>
              {hasDiscount && (
                <span className="text-xs text-gray-400 line-through">₹{product.price.toLocaleString()}</span>
              )}
            </div>
            {/* Rating */}
            <div className="flex items-center gap-1 mt-2">
              <div className="flex items-center bg-[#0F5132]/10 px-1.5 py-0.5 rounded text-xs">
                <span className="font-semibold text-[#0F5132]">4.5</span>
                <Star className="w-3 h-3 text-[#FFD700] fill-[#FFD700] ml-0.5" />
              </div>
              <span className="text-xs text-gray-400">(24)</span>
            </div>
          </CardContent>
        </div>
      </Card>
    </Link>
  );
}
